import React from "react";
import { Badge } from "@astryxdesign/core";
import type { BadgeVariant } from "@astryxdesign/core";
import styles from "./ScoreBadge.module.css";

export type ScoreBadgeTier = "high" | "mid" | "low" | "muted";

export interface ScoreBadgeProps {
  score: number | null;
  max: number;
  /**
   * Value display format.
   * - "fraction" (default): `score.toFixed(1)` + ` /max`, e.g. "4.2 /5"
   * - "percent": `score` rendered as-is + "%", e.g. "86%" — `score` must
   *   already be in 0–100 display units, because percent mode does not
   *   divide by `max`.
   * @default "fraction"
   */
  format?: "fraction" | "percent";
  /**
   * Tier override: when passed, it bypasses the ratio bands entirely, and the
   * caller is responsible for keeping the tier consistent with the displayed
   * value. When omitted, the tier is computed from `score`'s ratio to `max`.
   * "muted" always renders the neutral variant (e.g. for excluded questions).
   */
  tier?: ScoreBadgeTier;
  /**
   * Label shown when `score` is null.
   * @default "—"
   */
  nullLabel?: string;
}

type ComputedScoreTier = "high" | "mid" | "low";

const computeScoreTier = (score: number, max: number): ComputedScoreTier => {
  const ratio = score / max;
  if (ratio >= 0.8) return "high";
  if (ratio >= 0.4) return "mid";
  return "low";
};

const TIER_VARIANT: Record<ScoreBadgeTier, BadgeVariant> = {
  high: "success",
  mid: "warning",
  low: "error",
  muted: "neutral",
};

/**
 * Tiered score pill: green/amber/red by `score`'s ratio to `max` (or an
 * explicit `tier` override), or a neutral "—" when there's no
 * score yet. Astryx's Badge already exposes success/warning/error variants
 * that map directly onto the three tiers, so this is a thin wrapper rather
 * than a bespoke pill.
 *
 * Renders via Astryx's Badge, so this MUST be rendered inside an Astryx
 * <Theme> scope.
 */
const ScoreBadge = ({
  score,
  max,
  format = "fraction",
  tier,
  nullLabel = "—",
}: ScoreBadgeProps): React.ReactElement => {
  if (score == null) {
    return <Badge variant="neutral" label={nullLabel} />;
  }

  const variant = TIER_VARIANT[tier ?? computeScoreTier(score, max)];
  // Badge lays its children out with a flex gap (for icon + label), so the
  // whole label must be a single node — sibling fragments would inherit the
  // gap between them (e.g. "140 %").
  const label =
    format === "percent" ? (
      <span>
        {score}
        <span className={styles.denominator}>%</span>
      </span>
    ) : (
      <span>
        {score.toFixed(1)}
        <span className={styles.denominator}> /{max}</span>
      </span>
    );

  return <Badge variant={variant} label={label} />;
};

export default ScoreBadge;
